/**
 * Outfit categories for the Desktop mascot gallery card (OutfitCategoriesSection.tsx): the service's rules for an id, a
 * name and a yearly window (services/gyro-svc/src/desktopMascotCategoryRules.ts, which this file is tested against), what
 * the Categories panel sends (PUT /v1/admin/desktop/mascot/categories), and its wording. Owner, 2026-10-09: categories
 * can be turned on and off, and given a season ("nfl to be on from september to feb"); more categories are coming.
 *
 * No React, no network.
 */
import { MONTH_NAMES } from './desktopMascot';

/** A category id: a closed slug, 1 to 32 characters of lower-case letters, digits and hyphens, starting with a letter or digit. */
export const CATEGORY_ID_RE = /^[a-z0-9][a-z0-9-]{0,31}$/;
/** The group every outfit without a category is shown in: never a stored category. */
export const GENERAL_CATEGORY_ID = 'general';
export const GENERAL_CATEGORY_NAME = 'General';
export const CATEGORY_NAME_MAX = 40;
export const CATEGORIES_MAX = 50;
/** The most days each month has, February with its leap day. */
export const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/** Every year from `from` to `to`, both "MM-DD" and both included; `from` after `to` wraps the year end. */
export interface CategoryWindow {
  from: string;
  to: string;
}

/** A category as the panel edits it and PUT …/categories takes it. */
export interface OutfitCategory {
  id: string;
  name: string;
  enabled: boolean;
  window: CategoryWindow | null;
}

/** A category as the service lists it: with whether it is live today (Pacific time) and how many catalog outfits it holds. */
export interface AdminCategory extends OutfitCategory {
  live: boolean;
  outfitCount: number;
}

/** GET and PUT /v1/admin/desktop/mascot/categories, and `categories` on the gallery catalog. */
export interface CategoriesView {
  /** The revision every save sends back as expectedRevision; a stale one is refused with 409 mascot_categories_changed. */
  revision: number;
  /** Today's month-day in Pacific time, "MM-DD": the date `live` was judged on. */
  today: string;
  categories: AdminCategory[];
  /** By outfit id, every assignment stored. */
  outfits: Record<string, string>;
}

export interface SaveCategoriesBody {
  categories: OutfitCategory[];
  expectedRevision: number;
}

/** PUT /v1/admin/desktop/mascot/categories/assign: these outfits into one category (null: General). */
export interface AssignCategoryBody {
  category: string | null;
  outfitIds: string[];
}

export interface AssignCategoryResult {
  category: string | null;
  assigned: string[];
  unchanged: string[];
}

const MONTH_DAY_RE = /^(\d{2})-(\d{2})$/;
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

/** Whether a value is a month-day, "MM-DD", that exists in some year ("02-29" included). */
export function isMonthDay(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = MONTH_DAY_RE.exec(value);
  if (!match) return false;
  const month = Number(match[1]);
  const day = Number(match[2]);
  return month >= 1 && month <= 12 && day >= 1 && day <= DAYS_IN_MONTH[month - 1]!;
}

/** A month-day as a number that orders within the year: month * 100 + day ("09-01" is 901). */
export function monthDayNumber(monthDay: string): number {
  return Number(monthDay.slice(0, 2)) * 100 + Number(monthDay.slice(3, 5));
}

/** A calendar date as "MM-DD". */
export function monthDayText(month: number, day: number): string {
  return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The month (1 to 12) and day of a "MM-DD". */
export function monthDayParts(monthDay: string): { month: number; day: number } {
  return { month: Number(monthDay.slice(0, 2)), day: Number(monthDay.slice(3, 5)) };
}

/**
 * Whether a date (month * 100 + day) is inside a window, both ends included; a window whose `from` is after its `to`
 * wraps the year end ("09-01" to "02-28" holds from September 1 through February 28). The service's rule.
 */
export function inCategoryWindow(window: CategoryWindow, monthDay: number): boolean {
  const from = monthDayNumber(window.from);
  const to = monthDayNumber(window.to);
  return from <= to ? monthDay >= from && monthDay <= to : monthDay >= from || monthDay <= to;
}

/** Whether a category is live on a date: enabled, and inside its window when it has one. The service's rule. */
export function categoryLiveOn(category: Pick<OutfitCategory, 'enabled' | 'window'>, monthDay: number): boolean {
  return category.enabled && (category.window === null || inCategoryWindow(category.window, monthDay));
}

/** The day that means the end of February every year, leap year or not (the service holds a window through it either way). */
export const END_OF_FEBRUARY = '02-29';
export const END_OF_FEBRUARY_TEXT = 'the end of February';

/** A month-day in words: "September 1"; "02-29" is "the end of February". */
export function monthDayWords(monthDay: string): string {
  if (monthDay === END_OF_FEBRUARY) return END_OF_FEBRUARY_TEXT;
  const { month, day } = monthDayParts(monthDay);
  return `${MONTH_NAMES[month - 1] ?? '?'} ${day}`;
}

/** A category's season in words: "All year", or "September 1 to February 28, every year". */
export function categoryWindowText(window: CategoryWindow | null): string {
  if (!window) return 'All year';
  if (window.from === window.to) return `${monthDayWords(window.from)} only, every year`;
  return `${monthDayWords(window.from)} to ${monthDayWords(window.to)}, every year`;
}

/** What a category is doing now, as the panel's badge says it. */
export function categoryStatusText(category: Pick<AdminCategory, 'enabled' | 'window' | 'live'>): string {
  if (!category.enabled) return 'Switched off';
  if (category.live) return category.window ? 'In season' : 'Showing';
  return 'Out of season';
}

/**
 * The id for a new category, from the name an admin typed: lower case, letters and digits, hyphens between words, at
 * most 32 characters; '' when nothing usable is left. "NFL" is "nfl", "Star Wars Day" is "star-wars-day". The reserved
 * "general" becomes "general-outfits".
 */
export function categorySlug(name: string): string {
  const slug = name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32).replace(/-+$/g, '');
  return slug === GENERAL_CATEGORY_ID ? 'general-outfits' : slug;
}

/** An id for a new category that no existing category has: the slug, else the slug with -2, -3, … */
export function freshCategoryId(name: string, taken: ReadonlyArray<Pick<OutfitCategory, 'id'>>): string {
  const base = categorySlug(name);
  if (!base) return '';
  const used = new Set(taken.map((category) => category.id));
  if (!used.has(base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base.slice(0, 32 - String(n).length - 1)}-${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return '';
}

/** Why a category name would be refused, in plain words, or null. */
export function categoryNameRefusal(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'Give the category a name.';
  if (trimmed.length > CATEGORY_NAME_MAX) return `A category name is at most ${CATEGORY_NAME_MAX} characters.`;
  if (CONTROL_CHARACTERS.test(trimmed)) return 'A category name cannot contain control characters.';
  return null;
}

/** Why the registry as edited would be refused, in plain words, or null when it can be saved. */
export function categoriesRefusal(categories: ReadonlyArray<OutfitCategory>): string | null {
  if (categories.length > CATEGORIES_MAX) return `Gyro keeps at most ${CATEGORIES_MAX} categories.`;
  const seen = new Set<string>();
  const names = new Set<string>();
  for (const category of categories) {
    const named = categoryNameRefusal(category.name);
    if (named) return named;
    if (!CATEGORY_ID_RE.test(category.id) || category.id === GENERAL_CATEGORY_ID) return `“${category.name}” has an id Gyro cannot store (${category.id || 'empty'}).`;
    if (seen.has(category.id)) return `Two categories share the id “${category.id}”.`;
    seen.add(category.id);
    // The service refuses two categories with one name (trimmed, in any letter case): people could not tell the groups apart.
    const name = category.name.trim().toLowerCase();
    if (names.has(name)) return `Two categories are named “${category.name.trim()}” (letter case does not tell them apart).`;
    names.add(name);
    if (category.window && (!isMonthDay(category.window.from) || !isMonthDay(category.window.to))) return `“${category.name}” needs a real month and day at both ends of its season.`;
  }
  return null;
}

/** The PUT body for Save: names trimmed, each category as the panel holds it. */
export function categoriesSaveBody(categories: ReadonlyArray<OutfitCategory>, expectedRevision: number): SaveCategoriesBody {
  return {
    categories: categories.map((category) => ({
      id: category.id,
      name: category.name.trim(),
      enabled: category.enabled,
      window: category.window ? { from: category.window.from, to: category.window.to } : null,
    })),
    expectedRevision,
  };
}

/** Whether the panel's rows differ from what the service holds (so Save is offered). */
export function categoriesChanged(edited: ReadonlyArray<OutfitCategory>, stored: ReadonlyArray<OutfitCategory>): boolean {
  if (edited.length !== stored.length) return true;
  return edited.some((category, index) => {
    const known = stored[index]!;
    return category.id !== known.id || category.name.trim() !== known.name || category.enabled !== known.enabled
      || (category.window?.from ?? null) !== (known.window?.from ?? null) || (category.window?.to ?? null) !== (known.window?.to ?? null);
  });
}

/**
 * The season a new window starts with: September 1 through the end of February, the owner's example. "02-29" is the
 * end of February every year (a window holds through the month's last day, leap year or not), so the season never
 * stops a day short.
 */
export const DEFAULT_CATEGORY_WINDOW: CategoryWindow = { from: '09-01', to: '02-29' };

/** The name of an outfit's category for the card ("General" when it has none, or one the registry no longer has). */
export function categoryNameOf(categoryId: string | null | undefined, categories: ReadonlyArray<Pick<OutfitCategory, 'id' | 'name'>> | undefined): string {
  if (!categoryId) return GENERAL_CATEGORY_NAME;
  return categories?.find((category) => category.id === categoryId)?.name ?? GENERAL_CATEGORY_NAME;
}

/** What the card tells the admin after a category change was saved, or after the panel saved the registry. */
export const CATEGORIES_SAVED_TEXT = 'Saved. People see the change the next time they open Gyro’s outfits.';
export const CATEGORY_CHANGED_TEXT = 'Another admin changed the categories after you loaded them, so nothing was saved. Reload, check them, and make your change again.';
